import Button from 'components/app-components/ui/button';
import theraToast from 'components/generic-components/toast/TheraToast';
import { useModal } from 'hooks/useModal';
import CopyIcon2 from 'icons/CopyIcon2';
import DownloadIcon from 'icons/DownloadIcon';
import Tick from 'icons/Tick';
import { useEffect, useState } from 'react';
import TOTPInstructionsModal from './TOTPInstructionsModal';

const TwoFACodes = ({
  totpData,
  setHasUserSavedCodes,
}: {
  totpData: any;
  setHasUserSavedCodes: (value: boolean) => void;
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isSecretCopied, setIsSecretCopied] = useState(false);
  const instructionsModal = useModal();

  useEffect(() => {
    if (isCopied) {
      const timeoutId = setTimeout(() => {
        setIsCopied(false);
      }, 1200);

      return () => clearTimeout(timeoutId);
    }
  }, [isCopied]);

  useEffect(() => {
    if (isSecretCopied) {
      const timeoutId = setTimeout(() => {
        setIsSecretCopied(false);
      }, 1200);

      return () => clearTimeout(timeoutId);
    }
  }, [isSecretCopied]);

  const handleDownloadCodes = () => {
    let url: string;
    try {
      const codesText = totpData.recovery_codes.join('\n');
      const blob = new Blob([codesText], { type: 'text/plain' });
      url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'recovery-codes.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setHasUserSavedCodes(true);
    } catch (error) {
      theraToast.error({
        message: 'Failed to download recovery codes',
        error,
        options: {
          id: 'failed-to-download-recovery-codes',
        },
      });
    } finally {
      if (url) {
        window.URL.revokeObjectURL(url);
      }
    }
  };

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(totpData.secret);
      setIsSecretCopied(true);
      theraToast.success('Setup key copied to clipboard', {
        id: 'setup-key-copied-to-clipboard',
      });
    } catch (error) {
      theraToast.error({
        message: 'Failed to copy setup key',
        error,
        options: {
          id: 'failed-to-copy-setup-key',
        },
      });
    }
  };

  const handleCopyCodes = async () => {
    try {
      const codesText = totpData.recovery_codes.join('\n');
      await navigator.clipboard.writeText(codesText);
      setIsCopied(true);
      setHasUserSavedCodes(true);
    } catch (error) {
      theraToast.error({
        message: 'Failed to copy recovery codes',
        error,
        options: {
          id: 'failed-to-copy-recovery-codes',
        },
      });
    }
  };

  const firstHalf = totpData?.recovery_codes.slice(0, 5) || [];
  const secondHalf = totpData?.recovery_codes.slice(5) || [];

  return (
    <div className="rounded-xl !rounded-b-none border border-gray-200 p-4">
      <p className="mb-1 text-base font-medium text-neutral-900">Scan the QR</p>
      <p className="text-sm font-medium text-neutral-500">
        Use your preferred authenticator app. Scan the QR code or{' '}
        <button className="text-neutral-800 underline" onClick={handleCopySecret}>
          click here to copy the setup key.
        </button>{' '}
        <button className="text-neutral-800 underline" onClick={instructionsModal.handleOpen}>
          Need help setting this up?
        </button>
      </p>
      {instructionsModal.isOpen && (
        <TOTPInstructionsModal open={instructionsModal.isOpen} onClose={instructionsModal.handleClose} />
      )}
      <img src={totpData?.qr_code} alt="TOTP QR Code" className="mx-auto" />
      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-base font-medium text-neutral-900">Please save your recovery codes:</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="flex justify-between gap-4 text-xs">
            <div className="space-y-1">
              {firstHalf.map((code, index) => (
                <div key={index} className="text-xs font-medium">
                  {code}
                </div>
              ))}
            </div>
            <div className="space-y-1">
              {secondHalf.map((code, index) => (
                <div key={index} className="text-xs font-medium">
                  {code}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-4 flex gap-4">
          <Button
            variant="outline"
            onClick={handleDownloadCodes}
            size="xsmall"
            className="inline-flex items-center gap-2 text-neutral-900"
          >
            <DownloadIcon />
            Download
          </Button>
          <div className="flex items-center">
            <Button
              variant="outline"
              onClick={handleCopyCodes}
              size="xsmall"
              className="inline-flex items-center gap-2 text-neutral-900"
            >
              <CopyIcon2 />
              Copy
            </Button>
            {isCopied && <Tick className="ml-2" />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFACodes;
